import axios from 'axios';
import { resolveAdminAccessToken } from '../auth/adminAccessToken';
import { getAdminAuthServerUrl } from '../auth/adminOAuth';
import { normalizeAdminAccountUser, type AdminAccountUser } from '../types/account';
import { Q_LANGUAGE_APP_KEY } from './qLanguageOperations';

export type QLanguageUsageValue = {
  used: number;
  limit: number | null;
  remaining: number | null;
};

export type QLanguageEntitlementSource = 'STORE_PURCHASE' | 'PLAY_REVIEW' | 'NONE';

export type AdminQLanguageEntitlementUsage = {
  userId: string;
  appKey: string;
  productId: string | null;
  basePlanId: string | null;
  planCode: string | null;
  entitled: boolean;
  entitlementStatus: string;
  accessUntil: string | null;
  autoRenewing: boolean;
  entitlementSource: QLanguageEntitlementSource;
  internalTesterRegistered: boolean;
  internalTesterSubscriptionEnabled: boolean;
  usagePeriod: {
    startsAt: string | null;
    endsAt: string | null;
  };
  aiFeedback: QLanguageUsageValue;
  asrSeconds: QLanguageUsageValue;
  peerCallMinutes: QLanguageUsageValue;
};

export type AdminQLanguageInternalTesterRegistrationInput = {
  reason: string;
};

export const buildAdminQLanguageInternalTesterMutation = (
  method: 'PUT' | 'DELETE',
  userId: string,
  reason: string,
) => ({
  method,
  url: `/api/v1/admin/apps/${Q_LANGUAGE_APP_KEY}/users/${encodeURIComponent(userId)}/internal-tester`,
  data: { reason },
});

export const resolveAdminQLanguageEntitlementUserId = (
  user: Pick<AdminAccountUser, 'id'>,
): string => {
  const userId = user.id.trim();
  if (!userId) {
    throw new Error('Q Language 권한에 사용할 사용자 UUID가 없습니다.');
  }
  return userId;
};

const requireRecord = (value: unknown, field: string): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Q Language 응답의 ${field} 구조가 올바르지 않습니다.`);
  }
  return value as Record<string, unknown>;
};

const requireString = (value: unknown, field: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`Q Language 응답의 ${field} 값이 없습니다.`);
  }
  return value;
};

const requireNullableString = (value: unknown, field: string): string | null => {
  if (value === null || value === undefined) return null;
  return requireString(value, field);
};

const requireNumber = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Q Language 응답의 ${field} 값이 올바르지 않습니다.`);
  }
  return value;
};

const requireNullableNumber = (value: unknown, field: string): number | null => {
  if (value === null || value === undefined) return null;
  return requireNumber(value, field);
};

const requireBoolean = (value: unknown, field: string): boolean => {
  if (typeof value !== 'boolean') {
    throw new Error(`Q Language 응답의 ${field} 값이 올바르지 않습니다.`);
  }
  return value;
};

const normalizeUsage = (value: unknown, field: string): QLanguageUsageValue => {
  const record = requireRecord(value, field);
  return {
    used: requireNumber(record['used'], `${field}.used`),
    limit: requireNullableNumber(record['limit'], `${field}.limit`),
    remaining: requireNullableNumber(record['remaining'], `${field}.remaining`),
  };
};

const normalizeEntitlementSource = (value: unknown): QLanguageEntitlementSource => {
  if (value === 'STORE_PURCHASE' || value === 'PLAY_REVIEW' || value === 'NONE') {
    return value;
  }
  throw new Error('Q Language 응답의 entitlementSource 값이 올바르지 않습니다.');
};

export const normalizeAdminQLanguageEntitlementUsage = (
  payload: unknown,
): AdminQLanguageEntitlementUsage => {
  const record = requireRecord(payload, 'root');
  const appKey = requireString(record['appKey'], 'appKey');
  if (appKey !== Q_LANGUAGE_APP_KEY) {
    throw new Error(`Q Language appKey가 일치하지 않습니다: ${appKey}`);
  }

  const usagePeriod = requireRecord(record['usagePeriod'], 'usagePeriod');
  if (typeof record['autoRenewing'] !== 'boolean') {
    throw new Error('Q Language 응답의 autoRenewing 값이 올바르지 않습니다.');
  }
  if (typeof record['entitled'] !== 'boolean') {
    throw new Error('Q Language 응답의 entitled 값이 올바르지 않습니다.');
  }
  const userId = requireString(record['userId'], 'userId');
  const productId = requireNullableString(record['productId'], 'productId');
  const entitlementSource = normalizeEntitlementSource(record['entitlementSource']);
  const internalTesterRegistered = requireBoolean(
    record['internalTesterRegistered'],
    'internalTesterRegistered',
  );
  const internalTesterSubscriptionEnabled = requireBoolean(
    record['internalTesterSubscriptionEnabled'],
    'internalTesterSubscriptionEnabled',
  );
  if (record['entitled'] !== (entitlementSource !== 'NONE')) {
    throw new Error('Q Language 응답의 entitled와 entitlementSource가 일치하지 않습니다.');
  }
  if (entitlementSource === 'STORE_PURCHASE' && productId === null) {
    throw new Error('Q Language STORE_PURCHASE 응답에 productId가 없습니다.');
  }
  if (internalTesterSubscriptionEnabled && !internalTesterRegistered) {
    throw new Error('Q Language 내부 테스터 등록과 앱 구독 모드 상태가 일치하지 않습니다.');
  }
  // PLAY_REVIEW is retained as a rolling-compatibility source for both the old timed
  // review grant and the new tester-selected Plus mode. Only its store-product shape is
  // invariant; tester membership must be read from the two explicit booleans above.
  if (entitlementSource === 'PLAY_REVIEW' && productId !== null) {
    throw new Error('Q Language PLAY_REVIEW 응답에 스토어 상품이 포함되어 있습니다.');
  }

  return {
    userId,
    appKey,
    productId,
    basePlanId: requireNullableString(record['basePlanId'], 'basePlanId'),
    planCode: requireNullableString(record['planCode'], 'planCode'),
    entitled: record['entitled'],
    entitlementStatus: requireString(record['entitlementStatus'], 'entitlementStatus'),
    accessUntil: requireNullableString(record['accessUntil'], 'accessUntil'),
    autoRenewing: record['autoRenewing'],
    entitlementSource,
    internalTesterRegistered,
    internalTesterSubscriptionEnabled,
    usagePeriod: {
      startsAt: requireNullableString(usagePeriod['startsAt'], 'usagePeriod.startsAt'),
      endsAt: requireNullableString(usagePeriod['endsAt'], 'usagePeriod.endsAt'),
    },
    aiFeedback: normalizeUsage(record['aiFeedback'], 'aiFeedback'),
    asrSeconds: normalizeUsage(record['asrSeconds'], 'asrSeconds'),
    peerCallMinutes: normalizeUsage(record['peerCallMinutes'], 'peerCallMinutes'),
  };
};

export const getAdminQLanguageEntitlementUsage = async (
  userId: string,
): Promise<AdminQLanguageEntitlementUsage> => {
  const { customInstance } = await import('@edusense/payment-client/custom-axios');
  const payload = await customInstance<unknown>({
    method: 'GET',
    url: `/api/v1/admin/apps/${Q_LANGUAGE_APP_KEY}/users/${encodeURIComponent(userId)}/entitlement-usage`,
  }, {
    baseURL: '/',
  });
  return normalizeAdminQLanguageEntitlementUsage(payload);
};

export const findAdminQLanguageUserByEmail = async (
  email: string,
): Promise<AdminAccountUser> => {
  const normalizedEmail = email.trim();
  if (!normalizedEmail) {
    throw new Error('내부 테스터 이메일을 입력해주세요.');
  }

  const accessToken = resolveAdminAccessToken();
  const response = await axios.get<unknown>('/api/admin/users/by-email', {
    baseURL: getAdminAuthServerUrl(),
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    params: { email: normalizedEmail },
    withCredentials: true,
  });
  const user = normalizeAdminAccountUser(response.data);
  if (!user) {
    throw new Error('이메일 조회 응답에 사용자 식별자가 없습니다.');
  }
  if (user.email !== normalizedEmail) {
    throw new Error('조회한 사용자 이메일이 입력한 이메일과 일치하지 않습니다.');
  }
  return user;
};

export const registerAdminQLanguageInternalTester = async (
  userId: string,
  input: AdminQLanguageInternalTesterRegistrationInput,
): Promise<void> => {
  const { customInstance } = await import('@edusense/payment-client/custom-axios');
  await customInstance<unknown>(buildAdminQLanguageInternalTesterMutation(
    'PUT',
    userId,
    input.reason,
  ), {
    baseURL: '/',
  });
};

export const removeAdminQLanguageInternalTester = async (
  userId: string,
): Promise<void> => {
  const { customInstance } = await import('@edusense/payment-client/custom-axios');
  await customInstance<unknown>(buildAdminQLanguageInternalTesterMutation(
    'DELETE',
    userId,
    'Q Language internal tester registration removed by operator',
  ), {
    baseURL: '/',
  });
};
